/* ClientDashboard - Panel del usuario final
   Boss Console + Recarga oros + Push to GitHub + Settings */
import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useBranding } from "../BrandingContext";
import { ThemeToggle } from "../ThemeContext";
import { api, formatError } from "../api";
import BossConsole from "./BossConsole";
import SettingsTab from "./SettingsTab";
import Studio from "./Studio";
import AgentBuilder from "./AgentBuilder";

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  // Tab sincronizado con location.hash (#/settings, #/github, etc) para que
  // los enlaces internos (ej "Configurá tu GitHub") puedan saltar entre tabs.
  const hashToTab = (h) => {
    const key = (h || "").replace(/^#\/?/, "").trim();
    return ["chat", "recharge", "settings", "myapps"].includes(key) ? key : "chat";
  };
  // Si volvemos desde PayPal con ?paypal=success o ?paypal=cancel, forzamos
  // el tab "recharge" para que el RechargeTab procese el callback.
  const initialTab = () => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("paypal") === "success" || sp.get("paypal") === "cancel") {
      return "recharge";
    }
    return hashToTab(window.location.hash);
  };
  const [tab, setTab] = useState(initialTab);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    const onHash = () => setTab(hashToTab(window.location.hash));
    const onGoSettings = () => { window.location.hash = "#/settings"; setTab("settings"); };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("lluvia:goto-settings", onGoSettings);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("lluvia:goto-settings", onGoSettings);
    };
  }, []);

  const goTab = (k) => { setTab(k); window.location.hash = `#/${k}`; };

  useEffect(() => {
    api.get("/console/credits/me").then((r) => setBalance(r.data.balance)).catch(() => {});
    const t = setInterval(() => {
      api.get("/console/credits/me").then((r) => setBalance(r.data.balance)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, []);

  const brandName = branding?.product_name || "Lluvia App Studio";

  return (
    <div className="client-dash ls-v2-client" data-testid="client-dashboard">
      <header className="cd-header">
        <div className="cd-brand">
          <div className="cd-logo">{brandName.slice(0, 1)}</div>
          <div>
            <div className="cd-name">{brandName}</div>
            <div className="cd-user">{user?.name || user?.email}</div>
          </div>
        </div>
        <div className="cd-actions">
          {balance !== null && (
            <span className="cd-balance" data-testid="cd-balance">
              <strong>{balance}</strong> oros
            </span>
          )}
          <ThemeToggle />
          <button className="cd-logout" onClick={logout} data-testid="cd-logout">Salir</button>
        </div>
      </header>

      <div className="cd-tabs" data-testid="cd-tabs">
        {[
          ["chat", "Chat"],
          ["myapps", "Mis Apps"],
          ["recharge", "Recargar Oros"],
          ["settings", "Mi Cuenta"],
        ].map(([k, l]) => (
          <button
            key={k}
            className={`cd-tab ${tab === k ? "active" : ""}`}
            onClick={() => goTab(k)}
            data-testid={`cd-tab-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      <main className="cd-main">
        {tab === "chat" && <BossConsole />}
        {tab === "myapps" && <AgentBuilder />}
        {tab === "recharge" && <RechargeTab onTopup={() => api.get("/credits/me").then((r) => setBalance(r.data.balance))} />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

function RechargeTab({ onTopup }) {
  const [packs, setPacks] = useState({});
  const [activePromo, setActivePromo] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get("/paypal/packs").then((r) => {
      setPacks(r.data.packs || {});
      setActivePromo(r.data.active_promo);
    }).catch((e) => setErr(formatError(e)));

    // Capturar el callback de PayPal: cuando PayPal redirige con ?paypal=success&token=ORDER_ID,
    // llamamos a /paypal/capture/{order_id} para acreditar los oros.
    const params = new URLSearchParams(window.location.search);
    const paypalStatus = params.get("paypal");
    const orderId = params.get("token");
    if (paypalStatus === "success" && orderId) {
      setBusy("capturing");
      api.post(`/paypal/capture/${orderId}`).then((r) => {
        setSuccess({
          oros: r.data.credited_oros,
          balance: r.data.balance,
          already: r.data.already_processed,
        });
        if (onTopup) onTopup();
      }).catch((e) => setErr(`Error capturando pago: ${formatError(e)}`))
        .finally(() => {
          setBusy("");
          // Limpiar la URL para que un refresh no reintente capturar
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, "", cleanUrl);
        });
    } else if (paypalStatus === "cancel") {
      setErr("Cancelaste el pago. Si querés intentar de nuevo, elegí un pack abajo.");
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (key) => {
    setBusy(key);
    setErr("");
    try {
      const { data } = await api.post("/paypal/create-order", { pack: key });
      if (data.approve_url) {
        window.location.href = data.approve_url;
      }
    } catch (e) {
      setErr(formatError(e));
      setBusy("");
    }
  };

  return (
    <div className="recharge-tab" data-testid="recharge-tab">
      <h2>Recarga tu cuenta</h2>
      <p className="hero-sub">
        Los oros se descuentan por cada mensaje, voz o acción que pides a los agentes.
        Sin caducidad, sin suscripción. Pagas solo cuando los necesitas.
      </p>
      {activePromo && (
        <div className="promo-banner" data-testid="active-promo">
          🎉 {activePromo.label} — descuento aplicado automáticamente
        </div>
      )}
      {busy === "capturing" && (
        <div className="alert" style={{ background: "#FEF3C7", borderColor: "#FCD34D", color: "#92400E" }}
             data-testid="paypal-capturing">
          ⏳ Confirmando tu pago con PayPal... no cierres la ventana.
        </div>
      )}
      {success && (
        <div className="alert" style={{ background: "#D1FAE5", borderColor: "#34D399", color: "#065F46" }}
             data-testid="paypal-success">
          ✅ {success.already
            ? "Este pago ya estaba procesado."
            : `¡Pago confirmado! Acreditamos ${success.oros} oros a tu cuenta.`}
          <strong> Saldo actual: {success.balance} oros.</strong>
        </div>
      )}
      {err && <div className="alert">{err}</div>}
      <div className="packs-grid">
        {Object.entries(packs).map(([key, p]) => (
          <div key={key} className={`pack-card ${p.popular ? "popular" : ""}`} data-testid={`pack-${key}`}>
            {p.popular && <div className="pack-tag">Más elegido</div>}
            <h3>{p.label}</h3>
            <div className="pack-price">${p.price_usd}<small>USD</small></div>
            <div className="pack-oros"><strong>{p.oros}</strong> oros</div>
            {p.original_oros && p.original_oros !== p.oros && (
              <div className="pack-saving">+{p.oros - p.original_oros} oros bonus</div>
            )}
            <button
              className="pack-buy"
              onClick={() => buy(key)}
              disabled={busy === key}
              data-testid={`buy-${key}`}
            >
              {busy === key ? "Conectando con PayPal..." : "Comprar con PayPal"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

